import React, { useEffect, useState, useCallback } from 'react';
import { userAPI, taskAPI, activityLogAPI } from '../../services/api';
import { useAuth } from '../../AuthContext';
import './userActivityPage.css';

const ACTIVITY_TYPE_LABELS = [
    'Task Completed',  // 0
    'Task Created',    // 1
    'Status Changed',  // 2
    'Document Uploaded', // 3
    'Photo Uploaded',  // 4
    'Note Added',      // 5
    'Task Cancelled',  // 6
];

const ACTIVITY_TYPE_ICONS = [
    'fa-check-circle',
    'fa-plus-circle',
    'fa-exchange-alt',
    'fa-file',
    'fa-camera',
    'fa-sticky-note',
    'fa-times-circle',
];

const UserActivityPage = ({ userId, onBack, onNavigateToProject }) => {
    const { isAdmin } = useAuth();
    const admin = isAdmin();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const res = await userAPI.getActivity(userId);
            setData(res.data);
        } catch (err) {
            console.error('Error loading user activity:', err);
            setError('Failed to load user activity.');
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => { load(); }, [load]);

    const handleDeleteTask = async (taskId, title) => {
        if (!window.confirm(`Delete task "${title}"? This can't be undone.`)) return;
        setBusyId(`task-${taskId}`);
        try {
            await taskAPI.delete(taskId);
            await load();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete task.');
        } finally {
            setBusyId(null);
        }
    };

    const handleDeleteLog = async (logId) => {
        if (!window.confirm('Delete this activity log entry? This removes it from the audit trail.')) return;
        setBusyId(`log-${logId}`);
        try {
            await activityLogAPI.delete(logId);
            await load();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete activity log.');
        } finally {
            setBusyId(null);
        }
    };

    const taskRoleLabel = (task) => {
        const labels = [];
        if (task.assignedToId === userId) labels.push('assigned');
        if (task.createdById === userId) labels.push('created');
        return labels.join(' / ');
    };

    if (loading) {
        return (
            <div className="user-activity-page">
                <button className="btn-back" onClick={onBack}>
                    <i className="fas fa-arrow-left"></i> Back
                </button>
                <div className="loading">Loading…</div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="user-activity-page">
                <button className="btn-back" onClick={onBack}>
                    <i className="fas fa-arrow-left"></i> Back
                </button>
                <div className="error-message">{error || 'User not found.'}</div>
            </div>
        );
    }

    const { user, tasks, recentActivity, counts } = data;

    return (
        <div className="user-activity-page">
            <button className="btn-back" onClick={onBack}>
                <i className="fas fa-arrow-left"></i> Back
            </button>

            <div className="ua-header">
                <div className="ua-header-info">
                    <h1 className="ua-header-name">
                        {user.username}
                        {user.role && <span className="ua-role-pill">{user.role}</span>}
                    </h1>
                    <p className="ua-header-meta">
                        {user.email}
                        {user.createdAt && ` · Member since ${new Date(user.createdAt).toLocaleDateString()}`}
                    </p>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* Counts grid */}
            <div className="ua-counts-grid">
                <div className="ua-count-card"><div className="ua-count-value">{counts.tasks}</div><div className="ua-count-label">Tasks</div></div>
                <div className="ua-count-card"><div className="ua-count-value">{counts.notesAuthored}</div><div className="ua-count-label">Notes</div></div>
                <div className="ua-count-card"><div className="ua-count-value">{counts.documentsUploaded}</div><div className="ua-count-label">Documents</div></div>
                <div className="ua-count-card"><div className="ua-count-value">{counts.emailsSent}</div><div className="ua-count-label">Emails Sent</div></div>
                <div className="ua-count-card"><div className="ua-count-value">{counts.automationsCreated}</div><div className="ua-count-label">Automations</div></div>
                <div className="ua-count-card"><div className="ua-count-value">{counts.projectsAsSalesRep}</div><div className="ua-count-label">As Sales Rep</div></div>
            </div>

            {/* Recent Activity */}
            <section className="ua-section">
                <h2 className="ua-section-title">
                    Recent Activity <span className="ua-section-count">({recentActivity.length})</span>
                </h2>
                {recentActivity.length === 0 ? (
                    <p className="ua-empty">No recent activity for this user.</p>
                ) : (
                    <ul className="ua-list">
                        {recentActivity.map(a => {
                            const busy = busyId === `log-${a.id}`;
                            return (
                                <li key={`log-${a.id}`} className="ua-list-row">
                                    <div className="ua-list-icon">
                                        <i className={`fas ${ACTIVITY_TYPE_ICONS[a.activityType] || 'fa-circle'}`}></i>
                                    </div>
                                    <div className="ua-list-info">
                                        <div className="ua-list-primary">
                                            <span className="ua-list-type">{ACTIVITY_TYPE_LABELS[a.activityType] || 'Activity'}</span>
                                            {a.projectName && (
                                                <button
                                                    type="button"
                                                    className="ua-link"
                                                    onClick={() => onNavigateToProject && onNavigateToProject(a.projectId)}
                                                >
                                                    {a.projectName}
                                                </button>
                                            )}
                                        </div>
                                        <div className="ua-list-secondary">{a.description}</div>
                                        <div className="ua-list-time">{new Date(a.createdAt).toLocaleString()}</div>
                                    </div>
                                    {admin && (
                                        <button
                                            type="button"
                                            className="ua-delete-btn"
                                            disabled={busy}
                                            onClick={() => handleDeleteLog(a.id)}
                                            title="Delete this activity log entry"
                                        >
                                            <i className="fas fa-trash-alt"></i>
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            {/* Tasks */}
            <section className="ua-section">
                <h2 className="ua-section-title">
                    Tasks <span className="ua-section-count">({tasks.length})</span>
                </h2>
                {tasks.length === 0 ? (
                    <p className="ua-empty">No tasks tied to this user.</p>
                ) : (
                    <ul className="ua-list">
                        {tasks.map(t => {
                            const busy = busyId === `task-${t.id}`;
                            return (
                                <li key={`task-${t.id}`} className="ua-list-row">
                                    <div className="ua-list-icon">
                                        <i className="fas fa-tasks"></i>
                                    </div>
                                    <div className="ua-list-info">
                                        <div className="ua-list-primary">
                                            <span className="ua-list-type">{t.title}</span>
                                            <span className="ua-list-tag">{taskRoleLabel(t)}</span>
                                        </div>
                                        <div className="ua-list-secondary">
                                            {t.project && (
                                                <button
                                                    type="button"
                                                    className="ua-link"
                                                    onClick={() => onNavigateToProject && onNavigateToProject(t.project.id)}
                                                >
                                                    {t.project.projectName}
                                                </button>
                                            )}
                                            {t.dueDate && ` · due ${new Date(t.dueDate).toLocaleDateString()}`}
                                        </div>
                                        <div className="ua-list-time">Created {new Date(t.createdAt).toLocaleDateString()}</div>
                                    </div>
                                    {admin && (
                                        <button
                                            type="button"
                                            className="ua-delete-btn"
                                            disabled={busy}
                                            onClick={() => handleDeleteTask(t.id, t.title)}
                                            title="Delete this task"
                                        >
                                            <i className="fas fa-trash-alt"></i>
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            {!admin && (
                <p className="ua-readonly-note">Read-only — only admins can delete activity.</p>
            )}
        </div>
    );
};

export default UserActivityPage;
